import {
  ClipboardInputTransformationEvent,
  ClipboardPipeline,
  Plugin,
  DataProcessor,
  MatcherPattern,
  ViewDocument,
  ViewDocumentFragment,
  ViewElement,
  ViewNode,
  ViewDocumentEnterEvent,
} from "ckeditor5";

import PrettyHtmlData from "./PrettyHtmlData";

const cleanupView = (view: ViewDocumentFragment) => {
  const document = new ViewDocument(view.document.stylesProcessor);

  const [first, ...rest] = view.getChildren();
  let children: ViewNode[];

  if (first == null) {
    children = [];
  } else if (first.is("element") && first.name === "p") {
    //
    // If we have paragraphs, we're probably loading content that wasn't
    // created by single-line mode. Discard all except the first one
    //
    children = [...first.getChildren()];
  } else {
    //
    // Otherwise, wrap single-line content in a paragraph
    //
    children = [first, ...rest];
  }

  const brIndex = children.findIndex((e) => e.is("element") && e.name === "br");
  if (brIndex >= 0) {
    //
    // Discard first <br /> element, and everything after it
    //
    children.splice(brIndex);
  }

  children = children.filter(isAllowedFlowElement);

  const paragraph = new ViewElement(document, "paragraph", undefined, children);
  return new ViewDocumentFragment(document, [paragraph]);
};

const isAllowedFlowElement = (node: ViewNode) => {
  if (node.is("$text") || node.is("$textProxy")) return true;
  if (!node.is("element")) return false;

  return [
    "a",
    "abbr",
    "audio",
    "b",
    "code",
    "i",
    "img",
    "label",
    "mark",
    "math",
    "s",
    "small",
    "span",
    "strong",
    "sub",
    "sup",
    "svg",
    "u",
  ].includes(node.name);
};

class SingleLineDataProcessor implements DataProcessor {
  inner: DataProcessor;

  constructor(inner: DataProcessor) {
    this.inner = inner;
  }

  registerRawContentMatcher(pattern: MatcherPattern): void {
    this.inner.registerRawContentMatcher(pattern);
  }
  useFillerType(type: "default" | "marked"): void {
    this.inner.useFillerType(type);
  }
  get skipComments() {
    return this.inner.skipComments;
  }

  toData(viewFragment: ViewDocumentFragment): string {
    const children = Array.from(viewFragment.getChildren());
    if (
      children.length !== 1 ||
      !children[0].is("view:element") ||
      children[0].name !== "p"
    ) {
      //
      // Shouldn't happen; plugin should be ensuring that model always consists of a single
      // <paragraph>
      //
      return this.inner.toData(viewFragment);
    }

    return this.inner.toData(
      new ViewDocumentFragment(
        viewFragment.document,
        children[0].getChildren(),
      ),
    );
  }

  toView(data: string): ViewDocumentFragment {
    return cleanupView(this.inner.toView(data));
  }
}

export default class SingleLine extends Plugin {
  static get requires() {
    //
    // Depende on the default data processor so it doesn't load after us and wipe out our changes
    //
    return [PrettyHtmlData];
  }

  init() {
    const editor = this.editor;

    if (!editor.config.get("singleLine")) return;

    editor.data.processor = new SingleLineDataProcessor(editor.data.processor);

    editor.editing.view.document.on<ViewDocumentEnterEvent>(
      "enter",
      (e, data) => {
        data.preventDefault();
        e.stop();
      },
      { priority: "high" },
    );

    editor.plugins
      .get(ClipboardPipeline)
      .on<ClipboardInputTransformationEvent>(
        "inputTransformation",
        (e, data) => {
          data.content = cleanupView(data.content);
        },
      );
  }
}

declare module "ckeditor5" {
  interface EditorConfig {
    singleLine?: boolean;
  }
}
